import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, Play, Loader2, CheckCircle2, AlertTriangle, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";

export default function KimKimBackfillPage() {
  const qc = useQueryClient();
  const [lastResult, setLastResult] = useState(null);

  const statusQ = useQuery({
    queryKey: ["kimkim-backfill-status"],
    queryFn: async () => (await api.get("/admin/kimkim/backfill-legacy/status")).data,
  });

  const runMut = useMutation({
    mutationFn: async (force = false) => (await api.post(`/admin/kimkim/backfill-legacy?force=${force}`)).data,
    onSuccess: (data) => {
      setLastResult(data);
      const s = data.stats;
      toast.success(
        `Backfill completo · scraped ${s.scraped} · SOFI1 ${s.sofi1_migration}` +
        (s.no_data ? ` · sin datos ${s.no_data}` : "") +
        (s.errors ? ` · errores ${s.errors}` : ""),
        { duration: 8000 }
      );
      qc.invalidateQueries({ queryKey: ["kimkim-backfill-status"] });
      qc.invalidateQueries({ queryKey: ["bi-kimkim-summary"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const s = statusQ.data;

  return (
    <div className="space-y-5" data-testid="kimkim-backfill-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
          <Database className="h-6 w-6 text-primary" /> Backfill KimKim Pricing
        </h1>
        <p className="text-sm text-muted-foreground">
          Rellena los datos económicos KimKim de todos los viajes legacy usando la cascada:
          scrape del HTML → SOFI 1.0 (migración). Idempotente — solo procesa los que faltan.
        </p>
      </div>

      {/* Estado actual */}
      <Card data-testid="kimkim-backfill-status">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4 text-primary" /> Estado actual
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusQ.isLoading ? <Skeleton className="h-20 w-full" /> : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Trips KimKim totales</div>
                <div className="mt-1 text-2xl font-bold tabular-nums" data-testid="status-total">{s?.total_kimkim_trips ?? 0}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Procesados</div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-emerald-600" data-testid="status-processed">{s?.processed ?? 0}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pendientes</div>
                <div className={`mt-1 text-2xl font-bold tabular-nums ${(s?.pending || 0) > 0 ? "text-amber-600" : "text-muted-foreground"}`} data-testid="status-pending">{s?.pending ?? 0}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Detalle</div>
                <div className="mt-1 space-y-0.5 text-xs">
                  <div><Badge variant="outline" className="bg-emerald-50 text-emerald-800">Exacto (HTML)</Badge> <b className="tabular-nums">{s?.by_source?.scraped ?? 0}</b></div>
                  <div><Badge variant="outline" className="bg-amber-50 text-amber-800">SOFI 1.0</Badge> <b className="tabular-nums">{s?.by_source?.sofi1_migration ?? 0}</b></div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botón de acción */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" /> Ejecutar backfill
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-semibold text-foreground">Estrategia en cascada (sin estimaciones):</div>
            <ol className="ml-4 list-decimal space-y-0.5">
              <li>🥇 <b>Scrape del HTML</b> `/tp/{"{code}"}/markup-itemized-pricing` con la sesión KimKim activa. Precio, comisión, service fee y carbon removal exactos.</li>
              <li>🥈 <b>Migración SOFI 1.0</b> — si no hay HTML: <code>final_price_amount</code>, <code>income_amount</code>, <code>partner_margin_usd</code> convertido a EUR.</li>
            </ol>
            <div className="mt-2">Concurrencia limitada a 5 fetches simultáneos. Un backfill completo tarda ≈ 20-60s.</div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="lg" disabled={runMut.isPending || (s?.pending ?? 0) === 0} data-testid="run-backfill-btn">
                  {runMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                  {runMut.isPending ? "Ejecutando…" : `Ejecutar backfill (${s?.pending ?? 0} pendientes)`}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Ejecutar backfill KimKim?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se procesarán los <b>{s?.pending ?? 0}</b> viajes pendientes. La operación es idempotente
                    y puede tardar 20-60 segundos. Los datos se sobrescribirán solo si aún no tienen
                    <code className="mx-1">kimkim_pricing_source</code> seteado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => runMut.mutate(false)} data-testid="confirm-run-backfill">Ejecutar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Último resultado */}
      {lastResult && (
        <Card data-testid="last-backfill-result">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Último resultado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {Object.entries(lastResult.stats).map(([k, v]) => (
                <div key={k} className="rounded-md border bg-muted/20 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                  <div className={`mt-0.5 text-lg font-bold tabular-nums ${
                    k === "errors" && v > 0 ? "text-red-600" :
                    k === "scraped" ? "text-emerald-600" :
                    k === "sofi1_migration" ? "text-amber-600" :
                    "text-foreground"}`}>{v}</div>
                </div>
              ))}
            </div>
            {lastResult.details_truncated && (
              <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" /> Se muestran los primeros 200 detalles de {lastResult.stats.total}.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
